import { useEffect, useState } from 'react'
import { useOrientationPref } from '../state/settings'

export type ViewportOrientation = 'portrait' | 'landscape'

/**
 * Track the current viewport orientation via `matchMedia('(orientation: portrait)')`.
 *
 * Used by PixiGame to decide whether to rotate the stage 90° so a phone-portrait
 * viewport shows the 17×10 board with its long axis vertical (Phase 1.5.2 D1).
 *
 * Engine grid logic stays `17×10` regardless of orientation; only the rendered
 * Pixi stage rotates. Server replay never sees orientation — see ralplan
 * §"Three architectural options" for why D1 wins over D2/D3.
 */
export function useViewportOrientation(): ViewportOrientation {
  const [orientation, setOrientation] = useState<ViewportOrientation>(() => {
    if (typeof window === 'undefined') return 'landscape'
    return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(orientation: portrait)')
    const onChange = (ev: MediaQueryListEvent): void => {
      setOrientation(ev.matches ? 'portrait' : 'landscape')
    }
    // `addEventListener` is the modern API; Safari < 14 used `addListener`.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => {
        mql.removeEventListener('change', onChange)
      }
    }
    // Fallback for older Safari.
    mql.addListener(onChange)
    return () => {
      mql.removeListener(onChange)
    }
  }, [])

  return orientation
}

/**
 * Returns the effective orientation: the user's stored preference when it is
 * `'landscape'` or `'portrait'`, otherwise the auto-detected viewport
 * orientation from `useViewportOrientation`.
 *
 * Phase 1.5.5: this is the source consumed by `PixiGame`. Game routes
 * snapshot the value at start time so toggling the preference mid-match
 * does not flip the active board.
 */
export function useEffectiveOrientation(): ViewportOrientation {
  const [pref] = useOrientationPref()
  const auto = useViewportOrientation()
  return pref === 'auto' ? auto : pref
}
